import { schema } from 'normalizr';
import paginateAction from '@utils/paginateAction';
import { HOST_TYPE } from '@utils/api-helper/host';
import { batchTypes } from '../constants';
import { batchSchema, paginatorListSchema, itemSchema } from '../schemas';

export const fetchBatchData = (batchUID) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_BATCH_INFO,
    endpoint: `v3/batch/${batchUID}/details`,
    method: 'get',
    key: batchUID,
    schema: new schema.Object({ batch: batchSchema }),
    entityType: 'batch'
  });

export const fetchTopicsCovered = (batchUID) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_TOPICS_COVERED,
    endpoint: `v1/batch/${batchUID}/topics/?level=30`,
    method: 'get',
    key: batchUID
  });

export const fetchTopicsWithChildren = (batchUID, topicUID) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_TOPICS_WITH_CHILDREN,
    endpoint: `v1/batch/${batchUID}/children/?parent_uid=${topicUID}`,
    method: 'get',
    key: `${batchUID}_${topicUID}`
  });

const getItemsInCourse =
  (courseUID, conceptUID, resetWithoutEmpty) => (next) => ({
    type: batchTypes.FETCH_ITEMS_IN_COURSE,
    endpoint: next,
    method: 'get',
    key: `${conceptUID}_${courseUID}`,
    resetWithoutEmpty,
    schema: paginatorListSchema,
    directPaginatedEntity: true,
    entityType: 'item',
    processData: (data) => {
      const newData = { ...data, results: [] };
      if (data.results) {
        data.results.map((item) => {
          const itemData = {
            ...item,
            ...item.properties,
            courseUID: item.properties.programme.uid,
            courseSlug: item.properties.programme.slug,
            goal: item.properties.programme.goal
          };
          delete itemData.properties;
          delete itemData.programme;
          newData.results.push(itemData);
          return itemData;
        });
      }
      return newData;
    }
  });

export const fetchItemsInCourse = (
  batchUID,
  conceptUID,
  courseUID,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/batch/${batchUID}/lesson_of_concept_in_programme/?concept_uid=${conceptUID}&collection_uid=${courseUID}`,
    getItemsInCourse(courseUID, conceptUID, resetWithoutEmpty),
    'itemsInCourse',
    `${conceptUID}_${courseUID}`,
    resetWithoutEmpty
  )(nextPage);

export const enrollInBatch = (batchUID, enroll) => (dispatch) =>
  dispatch({
    type: batchTypes.ENROLL_IN_BATCH,
    endpoint: `v1/batch/${batchUID}/enroll/`,
    method: 'post',
    batchUID,
    enroll,
    data: {
      enroll
    }
  });

const getSchedule = (batchUID, epoch, resetWithoutEmpty) => (next) => ({
  type: batchTypes.FETCH_SCHEDULE,
  endpoint: next,
  method: 'get',
  key: `${batchUID}_${epoch}`,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'item',
  processData: (data) => {
    const newData = { ...data, results: [] };
    if (data.results) {
      data.results.map((item) => {
        const itemData = {
          ...item,
          ...item.properties,
          courseUID: item.properties.programme.uid,
          courseSlug: item.properties.programme.slug,
          goal: item.properties.programme.goal
        };
        delete itemData.properties;
        delete itemData.programme;
        newData.results.push(itemData);
        return itemData;
      });
    }
    return newData;
  }
});

export const fetchSchedule = (
  batchUID,
  epoch,
  timezoneDifference,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/batch/${batchUID}/schedule/?offset=${epoch}&timezone_difference=${timezoneDifference}&limit=6`,
    getSchedule(batchUID, epoch, resetWithoutEmpty),
    'batchSchedule',
    batchUID,
    resetWithoutEmpty
  )(nextPage);

const getTestSeries = (batchUID, resetWithoutEmpty) => (next) => ({
  type: batchTypes.FETCH_TEST_SERIES,
  endpoint: next,
  method: 'get',
  key: batchUID,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'course'
});

export const fetchTestSeries = (
  batchUID,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/batch/${batchUID}/test_series?limit=100`,
    getTestSeries(batchUID, resetWithoutEmpty),
    'batchTestSeries',
    batchUID,
    resetWithoutEmpty
  )(nextPage);

const getCoursesInConcept = (key, resetWithoutEmpty) => (next) => ({
  type: batchTypes.FETCH_COURSES_IN_CONCEPT,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'course'
});

export const fetchCoursesInConcept = (
  batchUID,
  conceptUID,
  nextPage,
  resetWithoutEmpty = false
) => {
  const key = `${batchUID}_${conceptUID}`;
  return paginateAction(
    `v1/batch/${batchUID}/lessons_in_concepts/?concept_uid=${conceptUID}&limit=3`,
    getCoursesInConcept(key, resetWithoutEmpty),
    'conceptCourses',
    key,
    resetWithoutEmpty
  )(nextPage);
};

export const fetchCalendarData = (batchUID, time) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_CALENDAR_DATA,
    endpoint: `v1/batch/${batchUID}/count/?epoch_time=${Math.round(
      time / 1000
    )}&timezone_difference=330`,
    method: 'get',
    month: new Date(time).getMonth(),
    batchUID
  });

export const fetchScheduledClasses =
  (batchUID, url = '', direction, resetWithoutEmpty = false) =>
  (dispatch) =>
    dispatch({
      type: batchTypes.FETCH_SCHEDULED_CLASSES,
      endpoint: url,
      method: 'get',
      schema: {
        results: [{ item: itemSchema }]
      },
      key: batchUID,
      direction,
      resetWithoutEmpty,
      processData: (data) => {
        const newData = { ...data, results: [] };
        if (data.results) {
          data.results.map((item) => {
            const itemData = {
              ...item,
              ...item.properties,
              courseUID: item.properties.programme?.uid,
              courseSlug: item.properties.programme?.slug,
              goal: item.properties.programme?.goal
            };
            delete itemData.properties;
            delete itemData.programme;
            newData.results.push({ item: itemData });
            return itemData;
          });
        }
        return newData;
      }
    });

const getBatches = (goalUID, type, resetWithoutEmpty) => (next) => ({
  type: batchTypes.FETCH_BATCHES,
  endpoint: next,
  method: 'get',
  key: `batches_${goalUID}_${type}`,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'batch'
});

export const fetchBatches = (
  goalUID,
  type = 0,
  nextPage,
  resetWithoutEmpty = false,
  forceReset = false
) =>
  paginateAction(
    `v1/batch/lists/filter/?goal_uid=${goalUID}&type=${type}`,
    getBatches(goalUID, type, resetWithoutEmpty),
    'batches',
    `batches_${goalUID}_${type}`,
    resetWithoutEmpty,
    forceReset
  )(nextPage);

const getBatchUpdates = (batchUID, resetWithoutEmpty) => (next) => ({
  type: batchTypes.FETCH_BATCH_UPDATES,
  endpoint: next,
  method: 'get',
  key: batchUID,
  resetWithoutEmpty
});

export const fetchBatchUpdates = (
  batchUID,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/channel/batch/${batchUID}/batch_stories/?limit=7`,
    getBatchUpdates(batchUID, resetWithoutEmpty),
    'batchUpdates',
    batchUID,
    resetWithoutEmpty
  )(nextPage);

export const fetchSimilarBatches = (batchUID) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_SIMILAR_BATCHES,
    endpoint: `v1/batch/lists/similar/?batch_uid=${batchUID}`,
    method: 'get',
    batchUID,
    schema: paginatorListSchema,
    directPaginatedEntity: true,
    entityType: 'batch',
    processData: (data) => {
      const newData = { ...data, results: [] };
      if (data.results) {
        newData.results = data.results.slice(0, 4);
      }
      return newData;
    }
  });

export const fetchTrialClasses = (batchUID) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_TRIAL_CLASSES,
    endpoint: `v1/batch/${batchUID}/trial_classes/`,
    method: 'get',
    key: batchUID
  });

export const fetchBatchFaq =
  (key = 'batchesfaq') =>
  (dispatch) =>
    dispatch({
      type: batchTypes.FETCH_FAQS,
      endpoint: `subscription-faqs/${key}/`,
      method: 'get',
      hostType: HOST_TYPE.CMS
    });

export const fetchBatchFeedbacks = (batchUID) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_FEEDBACKS,
    endpoint: `v1/uplus/feedbacks/?limit=4&offset=0&goal_uid=${batchUID}`,
    method: 'get',
    key: batchUID
  });

export const enrollInTrialClasses = (batchUID) => (dispatch) =>
  dispatch({
    type: batchTypes.ENROLL_TRIAL_CLASSES,
    endpoint: `v1/batch/${batchUID}/enroll_in_trial_classes/`,
    method: 'post'
  });

export const fetchBatchScheduleWeek = (batchUID, week) => (dispatch) =>
  dispatch({
    type: batchTypes.FETCH_BATCH_SCHEDULE_WEEKS,
    endpoint: `v3/batch/${batchUID}/schedule/?week=${week}`,
    method: 'get',
    key: week
  });

export const updateBatchScheduleWeek = (batchUID, week) => (dispatch) =>
  dispatch({
    type: batchTypes.UPDATE_BATCH_SCHEDULE_WEEKS,
    endpoint: `v3/batch/${batchUID}/schedule/?week=${week}`,
    method: 'get',
    key: week
  });
